import React, { useState, useEffect, useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
    LayoutDashboard, Plus, Edit, BarChart, LineChart, PieChart, Table as TableIcon, Sparkles, Save, Trash2, Settings2, RefreshCw, CheckCircle, AlertCircle, Loader, X, Bot, Palette, ChevronDown, Filter
} from 'lucide-react';
import apiService from '../../services/apiService';
import { PageLoader } from '../../components/PageLoader';
import Modal from '../../components/Modal';

import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, LineElement, PointElement, ArcElement, Filler
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, LineElement, PointElement, ArcElement, Filler);

const ResponsiveGridLayout = WidthProvider(Responsive);

// --- Helper Components & Functions ---
const defaultChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { color: '#c9d1d9' } },
      title: { display: false },
    },
    scales: {
      x: { ticks: { color: '#c9d1d9' }, grid: { color: 'rgba(201, 209, 217, 0.1)' } },
      y: { ticks: { color: '#c9d1d9' }, grid: { color: 'rgba(201, 209, 217, 0.1)' } },
    },
};

const generateChartData = (type, data, accentColor = '#16e2c3') => {
  if (!data || data.length === 0) return null;
  const chartColors = [accentColor, '#82ca9d', '#ffc658', '#ff8042', '#8884d8'];
  switch (type) {
    case 'line': case 'bar': {
      return {
        labels: data.map(item => item.label),
        datasets: [{
            label: data[0]?.valueLabel || 'Value',
            data: data.map(item => item.value),
            backgroundColor: type === 'bar' ? accentColor : `${accentColor}80`,
            borderColor: accentColor, borderWidth: 2, fill: true,
          }],
      };
    }
    case 'pie': case 'donut': {
      return {
        labels: data.map(item => item.label),
        datasets: [{ data: data.map(item => item.value), backgroundColor: chartColors }],
      };
    }
    default: return null;
  }
};

const formatFieldName = (fieldName) => {
  if (!fieldName) return '';
  const replacements = { 'segments.date': 'Date', 'metrics.cost_micros': 'Cost ($)', 'metrics.ctr': 'CTR (%)', 'metrics.average_cpc': 'Avg. CPC ($)', 'totalUsers': 'Total Users', 'pageTitle': 'Page Title', 'ga:date': 'Date' };
  if (replacements[fieldName]) return replacements[fieldName];
  return fieldName.replace(/_/g, ' ').replace(/ga:|metrics\.|segments\.|dimensions\.|campaign\.|ad_group\./gi, '').trim().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

// --- Main Custom Hook for State Management ---
function useAnalyticsHub(activeWorkspace) {
  const [dashboards, setDashboards] = useState([]);
  const [activeDashboardId, setActiveDashboardId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('saved');
  const [refreshTriggers, setRefreshTriggers] = useState({});

  useEffect(() => {
    if (!activeWorkspace) return;
    let isMounted = true;
    async function fetchData() {
      setIsLoading(true);
      try {
        const fetchedDashboards = await apiService.fetchDashboards();
        if (!isMounted) return;
        if (fetchedDashboards.length > 0) {
          setDashboards(fetchedDashboards);
          setActiveDashboardId(fetchedDashboards[0]._id);
        } else {
          setDashboards([]);
          setActiveDashboardId(null);
        }
      } catch (err) { console.error('Failed to fetch dashboards', err);
      } finally { if (isMounted) setIsLoading(false); }
    }
    fetchData();
    return () => { isMounted = false; };
  }, [activeWorkspace]);

  const saveDashboardChanges = (dashboardId, updates) => {
    if (!dashboardId) {
      setSaveStatus('error'); return;
    }
    setSaveStatus('saving');
    apiService.updateDashboard(dashboardId, updates)
      .then(() => setTimeout(() => setSaveStatus('saved'), 1500))
      .catch(err => { console.error('Update dashboard failed', err); setSaveStatus('error'); });
  };

  const activeDashboard = dashboards.find(d => d._id === activeDashboardId);

  const addDashboard = () => {
    const newConfig = { name: `New Dashboard ${dashboards.length + 1}`, widgets: [], layouts: { lg: [] } };
    apiService.addDashboard(newConfig).then(created => {
      setDashboards(ds => [...ds, created]);
      setActiveDashboardId(created._id);
    }).catch(err => console.error('Add dashboard failed', err));
  };

  const updateDashboard = (id, updates) => {
    setDashboards(ds => ds.map(d => (d._id === id ? { ...d, ...updates } : d)));
    saveDashboardChanges(id, updates);
  };
  
  const addWidget = (widgetConfig) => {
    if (!activeDashboard) return;
    setDashboards(prevDashboards => {
      const newDashboards = prevDashboards.map(d => {
        if (d._id !== activeDashboardId) return d;
        const newWidget = { ...widgetConfig, id: `w_${Date.now()}` };
        const newWidgets = [...d.widgets, newWidget];
        const newLayoutsLg = [...(d.layouts?.lg || []), { i: newWidget.id, x: (newWidgets.length % 3) * 4, y: Infinity, w: 4, h: 2 }];
        return { ...d, widgets: newWidgets, layouts: { ...d.layouts, lg: newLayoutsLg } };
      });
      const updatedDashboard = newDashboards.find(d => d._id === activeDashboardId);
      if (updatedDashboard) saveDashboardChanges(activeDashboardId, { widgets: updatedDashboard.widgets, layouts: updatedDashboard.layouts });
      return newDashboards;
    });
  };

  const updateWidget = (wid, upd) => {
    if (!activeDashboard) return;
    const updated = dashboards.map(d => {
      if (d._id !== activeDashboardId) return d;
      const newWidgets = d.widgets.map(w => (w.id === wid ? { ...w, ...upd } : w));
      return { ...d, widgets: newWidgets };
    });
    setDashboards(updated);
    const { widgets } = updated.find(d => d._id === activeDashboardId);
    saveDashboardChanges(activeDashboardId, { widgets });
  };
  
  const deleteDashboard = id => {
    if (!window.confirm('Are you sure you want to permanently delete this dashboard?')) return;
    apiService.deleteDashboard(id).catch(err => console.error('Delete dashboard failed', err));
    const remainingDashboards = dashboards.filter(d => d._id !== id);
    setDashboards(remainingDashboards);

    if (activeDashboardId === id) {
        setActiveDashboardId(remainingDashboards.length > 0 ? remainingDashboards[0]._id : null);
    }
  };

  const removeWidget = wid => { if (!activeDashboard) return; const updated = dashboards.map(d => { if (d._id !== activeDashboardId) return d; const newWidgets = d.widgets.filter(w => w.id !== wid); const newLayouts = Object.fromEntries(Object.entries(d.layouts).map(([bp, layout]) => [bp, layout.filter(l => l.i !== wid)])); return { ...d, widgets: newWidgets, layouts: newLayouts }; }); setDashboards(updated); const { widgets, layouts } = updated.find(d => d._id === activeDashboardId); saveDashboardChanges(activeDashboardId, { widgets, layouts }); };
  const handleRefreshWidget = (widgetId) => { setRefreshTriggers(prev => ({...prev, [widgetId]: (prev[widgetId] || 0) + 1, })); };

  return {
    dashboards, activeDashboard, activeDashboardId, setActiveDashboardId, isLoading, saveStatus,
    refreshTriggers, addDashboard, deleteDashboard, updateDashboard, addWidget, removeWidget, updateWidget, handleRefreshWidget,
  };
}

// --- Main View Component ---
export default function AnalyticsView({ activeWorkspace }) {
  const [editingWidget, setEditingWidget] = useState(null);
  const hub = useAnalyticsHub(activeWorkspace);

  const handleEditWidget = (widget) => { setEditingWidget(widget); };
  const handleAddNewWidget = () => { setEditingWidget({ type: 'kpi', title: 'New Widget', data: { value: 0 }, accentColor: '#16e2c3' }); };
  const handleSaveWidget = (widgetToSave) => {
    if (widgetToSave.id) hub.updateWidget(widgetToSave.id, widgetToSave);
    else hub.addWidget(widgetToSave);
    setEditingWidget(null);
  };

  return (
    <div className="bg-[#040E22] text-white min-h-screen font-poppins flex">
      <div className="flex-1 flex flex-col transition-all duration-300" style={{ marginRight: editingWidget ? '384px' : '0' }}>
        <Header 
          dashboards={hub.dashboards} 
          activeDashboard={hub.activeDashboard} 
          onSelectDashboard={hub.setActiveDashboardId}
          onUpdateDashboardName={(name) => hub.updateDashboard(hub.activeDashboardId, { name })}
          onAddDashboard={hub.addDashboard} 
          onDeleteDashboard={() => hub.deleteDashboard(hub.activeDashboardId)}
          onAddNewWidget={handleAddNewWidget} 
          saveStatus={hub.saveStatus} 
        />
        <main className="flex-1 p-6 overflow-y-auto">
            {hub.isLoading ? <PageLoader text="Loading Analytics Hub..." /> :
             !hub.activeDashboard ? (
              <div className="flex items-center justify-center h-full text-center">
                <div>
                  <h2 className="text-2xl font-bold">No Dashboard Selected</h2>
                  <p className="text-slate-400 mt-2">Create a new dashboard to get started.</p>
                  <button onClick={hub.addDashboard} className="mt-6 inline-flex items-center bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-4 rounded-lg">
                    <Plus size={16} className="mr-2" /> Create Dashboard
                  </button>
                </div>
              </div>
            ) : (
              <DashboardGrid dashboard={hub.activeDashboard} onLayoutChange={layouts => hub.updateDashboard(hub.activeDashboardId, { layouts })} onRemoveWidget={hub.removeWidget} onEditWidget={handleEditWidget} onRefreshWidget={hub.handleRefreshWidget} refreshTriggers={hub.refreshTriggers} />
            )}
        </main>
      </div>
      <ConfigurationSidebar isOpen={!!editingWidget} widget={editingWidget} onClose={() => setEditingWidget(null)} onSave={handleSaveWidget} onDelete={hub.removeWidget} activeWorkspace={activeWorkspace}/>
    </div>
  );
}


// --- Configuration Sidebar ---
function ConfigurationSidebar({ isOpen, widget, onClose, onSave, onDelete, activeWorkspace }) {
  const [localWidget, setLocalWidget] = useState(null);
  const [fieldSearch, setFieldSearch] = useState('');
  const [availableFields, setAvailableFields] = useState([]);
  const [availableTables, setAvailableTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [offlineFilter, setOfflineFilter] = useState({ dateColumn: '', startDate: '', endDate: '' });
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [dragOver, setDragOver] = useState(null);
  const [configuringField, setConfiguringField] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  
  useEffect(() => { 
    if (widget) {
      const newWidget = JSON.parse(JSON.stringify(widget));
      setLocalWidget(newWidget);

      const query = newWidget.dataSources?.query;
      const tableId = query?.tableId;

      if (query) {
        setOfflineFilter({
          dateColumn: query.dateColumn || '',
          startDate: query.startDate || '',
          endDate: query.endDate || '',
        });
      }

      if (tableId) {
        apiService.fetchIntegrationFields('offline').then(tables => {
          const currentTable = tables.find(t => t.id === tableId);
          setSelectedTable(currentTable || null);
        });
      } else {
        setSelectedTable(null);
      }
    } else {
      setLocalWidget(null);
      setSelectedTable(null);
      setOfflineFilter({ dateColumn: '', startDate: '', endDate: '' });
    }
   }, [widget]);

  // --- FIX: This is the only useEffect hook that should be here. ---
  // The duplicate that was causing the error has been removed.
  useEffect(() => {
    const integration = localWidget?.dataSources?.integration;
    if (integration) {
      setIsLoadingFields(true);
      if (integration === 'offline') {
        apiService.fetchIntegrationFields(integration)
          .then(data => setAvailableTables(data || []))
          .catch(err => { console.error('Failed to fetch offline tables', err); setAvailableTables([]); })
          .finally(() => setIsLoadingFields(false));
      } else {
        apiService.fetchIntegrationFields(integration)
          .then(data => setAvailableFields(data || []))
          .catch(err => { console.error(`Failed to fetch fields for ${integration}`, err); setAvailableFields([]); })
          .finally(() => setIsLoadingFields(false));
      }
    } else {
      setAvailableFields([]);
      setAvailableTables([]);
    }
  }, [localWidget?.dataSources?.integration]);

  const availableIntegrations = useMemo(() => {
      const integrations = [];
      if (activeWorkspace?.settings?.googleRefreshToken) integrations.push({ key: 'googleAnalytics', name: 'Google Analytics' });
      if (activeWorkspace?.settings?.adwordsRefreshToken) integrations.push({ key: 'googleAds', name: 'Google Ads' });
      integrations.push({ key: 'offline', name: 'Offline Sources' });
      return integrations;
  }, [activeWorkspace]);
  
  const constructQueryFromBuilder = (builder, widgetType) => {
    const integration = localWidget?.dataSources?.integration;
    if (!integration || !builder) return '';

    if (integration === 'googleAnalytics') {
        const dateDimension = builder.dimensions.find(d => d.id === 'date');
        let dateRanges = [{ startDate: '30daysAgo', endDate: 'today' }];
        
        if (dateDimension?.config?.dateRange) {
            const rangeConf = dateDimension.config.dateRange;
            if(rangeConf.type === 'custom' && rangeConf.start && rangeConf.end) {
                dateRanges = [{ startDate: rangeConf.start, endDate: rangeConf.end }];
            } else if (typeof rangeConf === 'string') {
                dateRanges = [{ startDate: rangeConf, endDate: 'today' }];
            }
        }
        
        const dimensionFilter = { andGroup: { expressions: builder.dimensions.filter(d => d.config?.filter?.value).map(d => ({ filter: { fieldName: d.id, stringFilter: { matchType: d.config.filter.matchType, value: d.config.filter.value } } })) } };
        
        const query = {
            dimensions: widgetType === 'kpi' ? [] : builder.dimensions.map(d => ({ name: d.id })),
            metrics: builder.metrics.map(m => ({ name: m.id })),
            dateRanges: dateRanges,
        };

        if (dimensionFilter.andGroup.expressions.length > 0) query.dimensionFilter = dimensionFilter;
        
        if (widgetType !== 'kpi' && builder.dimensions[0]?.id === 'date') {
            query.orderBys = [{ dimension: { dimensionName: 'date' }, desc: false }];
        }

        return JSON.stringify(query, null, 2);
    }
    return '';
  };

  const handleSelectOfflineTable = (tableId) => {
    const table = availableTables.find(t => t.id === tableId);
    setSelectedTable(table || null);
    if (table) {
        const xAxis = table.headers?.[0] || '';
        const yAxis = table.headers?.[1] || table.headers?.[0] || '';
        setLocalWidget(prev => ({
            ...prev,
            dataSources: {
                ...prev.dataSources,
                query: { tableId: table.id, xAxis, yAxis }
            }
        }));
        setOfflineFilter({ dateColumn: '', startDate: '', endDate: '' });
    }
  };

  const handleOfflineFilterChange = (filterName, value) => {
      const newFilters = { ...offlineFilter, [filterName]: value };
      setOfflineFilter(newFilters);
      setLocalWidget(prev => ({
          ...prev,
          dataSources: {
              ...prev.dataSources,
              query: {
                  ...prev.dataSources.query,
                  ...newFilters
              }
          }
      }));
  };
  
  const handleAxisChange = (axis, value) => {
      setLocalWidget(prev => ({
          ...prev,
          dataSources: {
              ...prev.dataSources,
              query: { ...prev.dataSources.query, [axis]: value }
          }
      }));
  };
  
  const handleBuilderUpdate = (type, field, action) => {
    setLocalWidget(prev => {
        const currentBuilder = prev.builder || { dimensions: [], metrics: [] };
        let list = [...currentBuilder[type]]; 
        const exists = list.some(item => item.id === field.id);
        if (action === 'add' && !exists) list.push(field);
        if (action === 'remove') list = list.filter(item => item.id !== field.id);
        
        const newBuilder = { ...currentBuilder, [type]: list };
        const newQuery = constructQueryFromBuilder(newBuilder, prev.type);
        
        return { ...prev, builder: newBuilder, dataSources: { ...prev.dataSources, query: newQuery } };
    });
  };

  const handleSaveFieldConfig = (updatedField) => {
    setLocalWidget(prev => {
        const builder = prev.builder;
        const list = builder[updatedField.type + 's'];
        const newList = list.map(f => f.id === updatedField.id ? updatedField : f);
        const newBuilder = { ...builder, [updatedField.type + 's']: newList };
        const newQuery = constructQueryFromBuilder(newBuilder, prev.type);
        return {...prev, builder: newBuilder, dataSources: {...prev.dataSources, query: newQuery } };
    });
    setConfiguringField(null);
  };
  
  const handleGenerateFromAI = async () => {
    if (!aiPrompt || !localWidget.dataSources?.integration) return;
    setIsGenerating(true);
    try {
        const res = await apiService.generateAIQuery(localWidget.dataSources.integration, aiPrompt);
        const query = res.machineQuery?.query;
        if (!query) return;
        const dimensionIds = (query.dimensions || []).map(d => d.name);
        const metricIds = (query.metrics || []).map(m => m.name);
        const suggestedDimensions = availableFields.filter(f => dimensionIds.includes(f.id));
        const suggestedMetrics = availableFields.filter(f => metricIds.includes(f.id));
        setLocalWidget(prev => {
            const newBuilder = { dimensions: suggestedDimensions, metrics: suggestedMetrics };
            const newQuery = constructQueryFromBuilder(newBuilder, prev.type);
            return { ...prev, builder: newBuilder, dataSources: { ...prev.dataSources, query: newQuery }};
        });
    } catch (err) { console.error("Failed to get suggestion from AI.", err);
    } finally { setIsGenerating(false); }
  };

  const handleDragStart = (e, field) => { e.dataTransfer.setData("application/json", JSON.stringify(field)); };
  const handleDrop = (e, type) => { e.preventDefault(); setDragOver(null); const field = JSON.parse(e.dataTransfer.getData("application/json")); if(field.type === type.slice(0, -1)) handleBuilderUpdate(type, field, 'add'); };
  const handleDragOver = (e, type) => { e.preventDefault(); setDragOver(type); };

  if (!isOpen || !localWidget) return null;

  return (
    <>
      <div className={`fixed top-0 right-0 h-full bg-slate-800 border-l border-slate-700 w-96 z-40 transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
                <h2 className="text-lg font-semibold">{localWidget.id ? 'Edit Widget' : 'Add New Widget'}</h2>
                <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-700"><X size={18} /></button>
            </div>
            <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                <div>
                <label className="text-sm font-medium text-slate-300">Widget Title</label>
                <input type="text" value={localWidget.title || ''} onChange={e => setLocalWidget({...localWidget, title: e.target.value})} className="w-full mt-1 bg-slate-700 border border-slate-600 rounded-md p-2" />
                </div>
                <div>
                <label className="text-sm font-medium text-slate-300">Data Source</label>
                <select value={localWidget.dataSources?.integration || ''} onChange={e => setLocalWidget({...localWidget, dataSources: {integration: e.target.value}})} className="w-full mt-1 bg-slate-700 border border-slate-600 rounded-md p-2">
                    <option value="">Select Integration</option>
                    {availableIntegrations.map(int => (<option key={int.key} value={int.key}>{int.name}</option>))}
                </select>
                </div>
                <div className="flex gap-4">
                <div className="flex-1">
                    <label className="text-sm font-medium text-slate-300">Visualization</label>
                    <select value={localWidget.type || ''} onChange={e => setLocalWidget({...localWidget, type: e.target.value})} className="w-full mt-1 bg-slate-700 border border-slate-600 rounded-md p-2">
                    <option value="kpi">Scorecard</option>
                    <option value="bar">Bar Chart</option>
                    <option value="line">Line Chart</option>
                    <option value="pie">Pie Chart</option>
                    <option value="donut">Donut Chart</option>
                    <option value="table">Table</option>
                    </select>
                </div>
                <div>
                    <label className="text-sm font-medium text-slate-300">Accent</label>
                    <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={localWidget.accentColor || '#16e2c3'} onChange={e => setLocalWidget({...localWidget, accentColor: e.target.value})} className="w-10 h-10 p-0 border-none rounded-md bg-slate-700 cursor-pointer" />
                    </div>
                </div>
                </div>

                {localWidget.dataSources?.integration === 'offline' && (
                <div className="space-y-4 p-3 bg-slate-900/50 rounded-lg">
                    <div>
                    <label className="text-sm font-medium text-slate-300">Select Table</label>
                    <select className="w-full mt-1 bg-slate-700 border border-slate-600 rounded-md p-2" value={localWidget.dataSources.query?.tableId || ''} onChange={(e) => handleSelectOfflineTable(e.target.value)} >
                        <option value="">-- Choose a table --</option>
                        {availableTables.map(table => <option key={table.id} value={table.id}>{table.name}</option>)}
                    </select>
                    </div>
                    {selectedTable && (
                    <>
                        <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-slate-300">X-Axis</label>
                            <select className="w-full mt-1 bg-slate-700 border border-slate-600 rounded-md p-2" value={localWidget.dataSources.query.xAxis} onChange={e => handleAxisChange('xAxis', e.target.value)}>
                            {selectedTable.headers.map(h => <option key={h} value={h}>{h}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-300">Y-Axis</label>
                            <select className="w-full mt-1 bg-slate-700 border border-slate-600 rounded-md p-2" value={localWidget.dataSources.query.yAxis} onChange={e => handleAxisChange('yAxis', e.target.value)}>
                            {selectedTable.headers.map(h => <option key={h} value={h}>{h}</option>)}
                            </select>
                        </div>
                        </div>
                        <div className="space-y-2 pt-2 border-t border-slate-700/50">
                        <label className="text-sm font-medium text-slate-300">Date Filter (Optional)</label>
                        <div>
                            <label className="text-xs text-slate-400">Date Column</label>
                            <select className="w-full mt-1 bg-slate-700 border border-slate-600 rounded-md p-2" value={offlineFilter.dateColumn} onChange={e => handleOfflineFilterChange('dateColumn', e.target.value)}>
                            <option value="">-- Select Date Column --</option>
                            {selectedTable.headers.map(h => <option key={h} value={h}>{h}</option>)}
                            </select>
                        </div>
                        {offlineFilter.dateColumn && (
                            <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs text-slate-400">Start Date</label>
                                <input type="date" className="w-full mt-1 bg-slate-700 border border-slate-600 rounded-md p-2" value={offlineFilter.startDate} onChange={e => handleOfflineFilterChange('startDate', e.target.value)} />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400">End Date</label>
                                <input type="date" className="w-full mt-1 bg-slate-700 border border-slate-600 rounded-md p-2" value={offlineFilter.endDate} onChange={e => handleOfflineFilterChange('endDate', e.target.value)} />
                            </div>
                            </div>
                        )}
                        </div>
                    </>
                    )}
                </div>
                )}

                {localWidget.dataSources?.integration && localWidget.dataSources?.integration !== 'offline' && (
                <div className="space-y-4">
                    <div>
                        <label className="text-sm font-medium text-slate-300 flex items-center gap-2"><Bot size={14} /> AI Data Helper</label>
                        <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} placeholder="e.g., 'show me users by country for the last 14 days'" className="w-full mt-1 bg-slate-700 border border-slate-600 rounded-md p-2 h-20" />
                        <button onClick={handleGenerateFromAI} disabled={isGenerating} className="w-full mt-2 flex items-center justify-center bg-teal-600/80 hover:bg-teal-600 text-white font-bold py-2 px-4 rounded-lg disabled:bg-slate-600 disabled:cursor-not-allowed">
                            {isGenerating ? <Loader size={16} className="animate-spin mr-2" /> : <Sparkles size={16} className="mr-2" />} Generate
                        </button>
                    </div>
                    <div className="text-center text-xs text-slate-500">OR</div>
                    <div>
                        <label className="text-sm font-medium text-slate-300">Manually Select Fields</label>
                        <div className="mt-2 space-y-1 max-h-40 overflow-y-auto pr-2">
                            {isLoadingFields ? <div className="text-center text-slate-400 p-4"><Loader className="animate-spin" /></div> :
                            availableFields.map(field => ( 
                                <div key={field.id} draggable="true" onDragStart={(e) => handleDragStart(e, field)} className={`w-full text-left p-2 rounded-md flex justify-between items-center text-sm bg-slate-700 hover:bg-slate-600 cursor-grab`}>
                                    <span>{field.name}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${field.type === 'metric' ? 'bg-green-800' : 'bg-blue-800'}`}>{field.type}</span>
                                </div> 
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-slate-300">Builder</label>
                        <div className="mt-1 p-2 bg-slate-900/50 rounded-md space-y-2">
                            <div onDrop={(e) => handleDrop(e, 'dimensions')} onDragOver={(e) => handleDragOver(e, 'dimensions')} onDragLeave={() => setDragOver(null)} className={`p-2 border border-dashed rounded-md min-h-[40px] flex flex-wrap gap-2 transition-colors ${dragOver === 'dimensions' ? 'border-teal-400 bg-teal-900/50' : 'border-slate-600'}`}>
                                {localWidget.builder?.dimensions.map(d => ( 
                                    <button key={d.id} onClick={() => setConfiguringField(d)} className="bg-blue-800 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1 hover:bg-blue-700">
                                        {d.name} {d.config?.filter?.value || d.config?.dateRange ? <Filter size={10} /> : null} 
                                        <X size={12} className="ml-1" onClick={(e) => {e.stopPropagation(); handleBuilderUpdate('dimensions', d, 'remove')}} />
                                    </button> 
                                ))}
                            </div>
                            <div onDrop={(e) => handleDrop(e, 'metrics')} onDragOver={(e) => handleDragOver(e, 'metrics')} onDragLeave={() => setDragOver(null)} className={`p-2 border border-dashed rounded-md min-h-[40px] flex flex-wrap gap-2 transition-colors ${dragOver === 'metrics' ? 'border-teal-400 bg-teal-900/50' : 'border-slate-600'}`}>
                            {localWidget.builder?.metrics.map(m => ( 
                                    <div key={m.id} className="bg-green-800 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
                                        {m.name} 
                                        <X size={12} className="cursor-pointer" onClick={() => handleBuilderUpdate('metrics', m, 'remove')} />
                                    </div> 
                            ))}
                            </div>
                        </div>
                    </div>
                </div>
                )}
            </div>
            <div className="p-4 border-t border-slate-700 flex justify-between items-center">
                <button onClick={() => { if(window.confirm('Are you sure?')) { onDelete(widget.id); onClose(); } }} className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-900/20 rounded-md">
                <Trash2 size={16} />
                </button>
                <button onClick={() => onSave(localWidget)} className="bg-teal-600 text-white font-bold py-2 px-4 rounded-md hover:bg-teal-700">
                Save Widget
                </button>
            </div>
        </div>
      </div>
      <FieldConfigModal isOpen={!!configuringField} field={configuringField} onSave={handleSaveFieldConfig} onClose={() => setConfiguringField(null)} />
    </>
  );
}

function FieldConfigModal({ isOpen, field, onSave, onClose }) {
    const [config, setConfig] = useState({});
    const [dateRangeType, setDateRangeType] = useState('preset');

    useEffect(() => {
        if(field) { const currentConfig = field.config || {}; setConfig(currentConfig); if(currentConfig.dateRange?.type === 'custom') setDateRangeType('custom'); else setDateRangeType('preset'); }
    }, [field]);

    if (!isOpen || !field) return null;

    const handleSave = () => { onSave({ ...field, config }); };

    const handleDateRangeChange = (value) => {
        if(value === 'custom') {
            setDateRangeType('custom');
            const endDate = new Date().toISOString().split('T')[0];
            const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            setConfig({ ...config, dateRange: { type: 'custom', start: startDate, end: endDate }});
        } else {
            setDateRangeType('preset');
            setConfig({ ...config, dateRange: value });
        }
    };

    const renderConfigOptions = () => {
        if(field.id === 'date') {
            return (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Date Range</label>
                        <select
                            className="w-full bg-slate-700 border border-slate-600 rounded-md p-2"
                            value={dateRangeType === 'preset' ? config.dateRange || '30daysAgo' : 'custom'}
                            onChange={(e) => handleDateRangeChange(e.target.value)}
                        >
                            <option value="7daysAgo">Last 7 Days</option>
                            <option value="30daysAgo">Last 30 Days</option>
                            <option value="90daysAgo">Last 90 Days</option>
                            <option value="custom">Custom Range</option>
                        </select>
                    </div>
                    {dateRangeType === 'custom' && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">Start Date</label>
                                <input type="date" className="w-full bg-slate-700 border border-slate-600 rounded-md p-2" value={config.dateRange?.start || ''} onChange={e => setConfig({...config, dateRange: {...config.dateRange, start: e.target.value}})} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">End Date</label>
                                <input type="date" className="w-full bg-slate-700 border border-slate-600 rounded-md p-2" value={config.dateRange?.end || ''} onChange={e => setConfig({...config, dateRange: {...config.dateRange, end: e.target.value}})} />
                            </div>
                        </div>
                    )}
                </div>
            )
        }

        if(field.type === 'dimension') {
            return (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Filter Match Type</label>
                        <select
                            className="w-full bg-slate-700 border border-slate-600 rounded-md p-2"
                            value={config.filter?.matchType || 'CONTAINS'}
                            onChange={(e) => setConfig({ ...config, filter: { ...config.filter, matchType: e.target.value }})}
                        >
                            <option value="CONTAINS">Contains</option>
                            <option value="EXACT">Exactly Matches</option>
                            <option value="BEGINS_WITH">Begins With</option>
                            <option value="DOES_NOT_CONTAIN">Does Not Contain</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Filter Value</label>
                        <input
                            type="text"
                            placeholder="e.g., /blog/"
                            className="w-full bg-slate-700 border border-slate-600 rounded-md p-2"
                            value={config.filter?.value || ''}
                            onChange={(e) => setConfig({ ...config, filter: { ...config.filter, value: e.target.value }})}
                        />
                    </div>
                </div>
            )
        }
        return <p className="text-slate-400">This field has no configuration options.</p>;
    }

    return ( <Modal isOpen={isOpen} onClose={onClose} title={`Configure: ${field.name}`}> <div className="space-y-6"> {renderConfigOptions()} <div className="flex justify-end gap-2 pt-4 border-t border-slate-700"> <button type="button" onClick={onClose} className="bg-slate-600 text-white font-bold py-2 px-4 rounded-md hover:bg-slate-500">Cancel</button> <button type="button" onClick={handleSave} className="bg-teal-600 text-white font-bold py-2 px-4 rounded-md hover:bg-teal-700">Apply</button> </div> </div> </Modal> );
}

function SaveStatusIndicator({ status }) { 
    if (status === 'saving') return <div className="flex items-center text-slate-400 text-sm"><Loader size={14} className="mr-2 animate-spin" /> Saving...</div>; 
    if (status === 'saved') return <div className="flex items-center text-green-400 text-sm"><CheckCircle size={14} className="mr-2" /> Saved</div>; 
    if (status === 'error') return <div className="flex items-center text-red-400 text-sm"><AlertCircle size={14} className="mr-2" /> Save Error</div>; 
    return null; 
};

function Header({ dashboards, activeDashboard, onSelectDashboard, onUpdateDashboardName, onAddDashboard, onDeleteDashboard, onAddNewWidget, saveStatus }) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [name, setName] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (activeDashboard) {
      setName(activeDashboard.name);
    }
  }, [activeDashboard]);

  const handleNameSave = () => {
    if (name.trim()) {
      onUpdateDashboardName(name.trim());
    }
    setIsEditingName(false);
  };

  if (!activeDashboard) {
    return (
      <header className="bg-[#0A142C]/80 backdrop-blur sticky top-0 z-30">
        <div className="flex items-center justify-between h-16 border-b border-slate-700 px-6">
          <h1 className="text-xl font-bold text-white">Analytics</h1>
           <button onClick={onAddDashboard} className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-4 rounded-lg text-sm"><Plus size={16} /> Create Dashboard</button>
        </div>
      </header>
    );
  }

  return (
    <header className="bg-[#0A142C]/80 backdrop-blur sticky top-0 z-30">
      <div className="flex items-center justify-between h-16 border-b border-slate-700 px-6">
        <div className="flex items-center gap-3">
          {isEditingName ? (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={handleNameSave}
              onKeyDown={(e) => e.key === 'Enter' && handleNameSave()}
              className="bg-slate-700 text-xl font-bold rounded-md px-2 -ml-2"
              autoFocus
            />
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-white">{activeDashboard.name}</h1>
              <button onClick={() => setIsEditingName(true)} className="text-slate-400 hover:text-white"><Edit size={16} /></button>
            </div>
          )}

          <div className="relative">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 rounded-md hover:bg-slate-700"><ChevronDown size={16} /></button>
            {isMenuOpen && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-slate-800 border border-slate-700 rounded-md shadow-lg z-50">
                <div className="p-1">
                  {dashboards.map(d => (
                    <button key={d._id} onClick={() => { onSelectDashboard(d._id); setIsMenuOpen(false); }} className={`w-full text-left px-3 py-2 text-sm rounded-md ${d._id === activeDashboard._id ? 'bg-teal-600' : 'hover:bg-slate-700'}`}>
                      {d.name}
                    </button>
                  ))}
                   <div className="border-t border-slate-700 my-1"></div>
                   <button onClick={() => { onAddDashboard(); setIsMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-slate-700 text-teal-400"> + Create New</button>
                   <button onClick={() => { onDeleteDashboard(); setIsMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-slate-700 text-red-400">Delete Current</button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <SaveStatusIndicator status={saveStatus} />
          <button onClick={onAddNewWidget} className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-4 rounded-lg text-sm">
            <Plus size={16} /> Add Widget
          </button>
        </div>
      </div>
    </header>
  );
}

function DashboardGrid({ dashboard, onLayoutChange, onRemoveWidget, onEditWidget, onRefreshWidget, refreshTriggers }) { 
    return ( 
        <ResponsiveGridLayout 
            className="layout" 
            layouts={dashboard.layouts} 
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }} 
            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }} 
            rowHeight={100} 
            draggableHandle=".drag-handle" 
            onLayoutChange={(layout, layouts) => onLayoutChange(layouts)}
        >
            {dashboard.widgets.map(w => ( 
                <div key={w.id} className="bg-slate-800 rounded-lg p-4 flex flex-col shadow-lg">
                    <WidgetHeader 
                        title={w.title} 
                        onRemove={() => onRemoveWidget(w.id)} 
                        onEdit={() => onEditWidget(w)} 
                        onRefresh={() => onRefreshWidget(w.id)} 
                    />
                    <div className="flex-grow overflow-auto">
                        <WidgetBody widget={w} refreshTrigger={refreshTriggers[w.id]} />
                    </div>
                </div> 
            ))}
        </ResponsiveGridLayout> 
    ); 
}

function WidgetHeader({ title, onRemove, onEdit, onRefresh }) { 
    return ( 
        <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-white truncate drag-handle cursor-move flex-grow">{title}</h3>
            <div className="flex items-center flex-shrink-0">
                <button type="button" onClick={e => { e.stopPropagation(); onRefresh(); }} className="text-slate-500 hover:text-teal-400 ml-2 cursor-pointer"><RefreshCw size={16} /></button>
                <button type="button" onClick={e => { e.stopPropagation(); onEdit(); }} className="text-slate-500 hover:text-teal-400 ml-2 cursor-pointer"><Settings2 size={16} /></button>
                <button type="button" onClick={e => { e.stopPropagation(); onRemove(); }} className="text-slate-500 hover:text-red-500 ml-2 cursor-pointer"><Trash2 size={16} /></button>
            </div>
        </div> 
    ); 
}

function WidgetBody({ widget, refreshTrigger }) { 
    const [data, setData] = useState(null); 
    const [isLoading, setIsLoading] = useState(true); 
    const [error, setError] = useState(null); 

    useEffect(() => { 
        const fetchDataForWidget = async () => { 
            if (!widget.dataSources?.integration || !widget.dataSources?.query) { 
                setData({ payload: widget.data || null, options: defaultChartOptions }); 
                setIsLoading(false); 
                return; 
            } 
            setIsLoading(true); 
            setError(null); 
            try { 
                const { data: result, xAxis, yAxis } = await apiService.fetchWidgetData({ 
                    integration: widget.dataSources.integration, 
                    query: widget.dataSources.query 
                }); 
                let formattedData = result; 
                let chartOptions = JSON.parse(JSON.stringify(defaultChartOptions)); 
                if (widget.type === 'kpi') { 
                    const singleRow = result?.[0]; 
                    const valueKey = yAxis; 
                    let value = singleRow && valueKey ? singleRow[valueKey] : 'N/A'; 
                    if ((valueKey === 'bounceRate' || valueKey === 'metrics.ctr') && typeof value === 'number') { 
                        value = `${(value * 100).toFixed(1)}%`; 
                    } else if (typeof value === 'number') { 
                        value = value.toLocaleString(); 
                    } 
                    formattedData = { value: value }; 
                } else if (Array.isArray(result) && ['line', 'bar', 'pie', 'donut'].includes(widget.type)) { 
                    const labelKey = xAxis, valueKey = yAxis; 
                    if (labelKey && valueKey) { 
                        formattedData = result.map(item => ({ 
                            label: item[labelKey] || 'N/A', 
                            value: item[valueKey] || 0, 
                            valueLabel: formatFieldName(valueKey) 
                        })); 
                        if (['line', 'bar'].includes(widget.type)) { 
                            chartOptions.scales.x.title = { display: true, text: formatFieldName(labelKey), color: '#c9d1d9' }; 
                            chartOptions.scales.y.title = { display: true, text: formatFieldName(valueKey), color: '#c9d1d9' }; 
                        } 
                    } 
                } 
                setData({ payload: formattedData, options: chartOptions }); 
            } catch (err) { 
                setError(err.response?.data?.message || 'Failed to load data.'); 
                setData({ payload: null, options: defaultChartOptions }); 
            } finally { 
                setIsLoading(false); 
            } 
        }; 
        fetchDataForWidget(); 
    // --- FINAL FIX ---
    // Stringify BOTH object dependencies to stabilize the effect hook.
    }, [widget.id, JSON.stringify(widget.data), widget.type, widget.dataSources?.integration, JSON.stringify(widget.dataSources?.query), refreshTrigger]); 
    
    if (isLoading) return <div className="w-full h-full flex items-center justify-center text-slate-400">Loading...</div>; 
    if (error) return <div className="w-full h-full flex items-center justify-center text-red-400 p-4 text-center">{error}</div>; 
    
    switch (widget.type) { 
        case 'kpi': return <KPICard data={data?.payload} accentColor={widget.accentColor} />; 
        case 'table': return <TableWidget data={data?.payload} />; 
        case 'line': return <LineChartWidget data={data?.payload} options={data?.options} accentColor={widget.accentColor} />; 
        case 'bar': return <BarChartWidget data={data?.payload} options={data?.options} accentColor={widget.accentColor} />; 
        case 'pie': return <PieChartWidget data={data?.payload} options={data?.options} accentColor={widget.accentColor} />; 
        case 'donut': return <DonutChartWidget data={data?.payload} options={data?.options} accentColor={widget.accentColor} />; 
        default: return <div className="w-full h-full flex items-center justify-center text-slate-400">Select a visualization type.</div>; 
    } 
}
const TableWidget = ({ data }) => { if (!Array.isArray(data) || data.length === 0 || !data[0]) return <div className="w-full h-full flex items-center justify-center text-slate-400">No data for table</div>; const headers = Object.keys(data[0]); return ( <div className="overflow-y-auto h-full"><table className="w-full text-sm text-left"><thead className="text-xs text-slate-400 uppercase bg-slate-700/50 sticky top-0"><tr>{headers.map(h => <th key={h} className="px-4 py-2">{formatFieldName(h)}</th>)}</tr></thead><tbody>{data.map((row, i) => (<tr key={i} className="border-b border-slate-700">{headers.map(h => <td key={h} className="px-4 py-2 font-medium truncate">{typeof row[h] === 'number' ? row[h].toLocaleString() : row[h]}</td>)}</tr>))}</tbody></table></div> ); };
const KPICard = ({ data, accentColor }) => ( <div className="flex flex-col items-center justify-center h-full"><p className="text-5xl font-bold" style={{color: accentColor || '#16e2c3'}}>{data?.value || 'N/A'}</p>{data?.change && <p className={`text-lg ${data.change.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>{data.change}</p>}</div> );
const LineChartWidget = ({ data, options, accentColor }) => { const chartData = generateChartData('line', data, accentColor); return chartData ? <Line data={chartData} options={options || defaultChartOptions} /> : <div className="text-center text-slate-400">No data</div>; };
const BarChartWidget = ({ data, options, accentColor }) => { const chartData = generateChartData('bar', data, accentColor); return chartData ? <Bar data={chartData} options={options || defaultChartOptions} /> : <div className="text-center text-slate-400">No data</div>; };
const PieChartWidget = ({ data, options, accentColor }) => { const chartData = generateChartData('pie', data, accentColor); return chartData ? <Pie data={chartData} options={options || defaultChartOptions} /> : <div className="text-center text-slate-400">No data</div>; };
const DonutChartWidget = ({ data, options, accentColor }) => { const chartData = generateChartData('donut', data, accentColor); return chartData ? <Doughnut data={chartData} options={options || defaultChartOptions} /> : <div className="text-center text-slate-400">No data</div>; };